import SEO from "../components/site/SEO";
import Reveal from "../components/site/Reveal";
import PricingTable from "../components/site/PricingTable";
import { useDevis } from "../components/site/DevisForm";
import { useConfig } from "../components/Layout";
import { IMAGES, TARIFS_DEFAUT } from "../lib/constants";

const SECTIONS = [
  {
    titre: "Vidange et révision",
    image: IMAGES.vidange,
    alt: "Vidange moteur : l'huile usée s'écoule dans le bac de récupération",
    texte: [
      "Entretien complet de votre véhicule : vidange moteur, remplacement des filtres (huile, air, habitacle, carburant), contrôle des niveaux et des points de sécurité.",
      "Nous respectons les spécifications du constructeur : huile adaptée, couples de serrage, carnet d'entretien mis à jour. Votre garantie constructeur est préservée.",
    ],
  },
  {
    titre: "Diagnostic automobile",
    image: IMAGES.diagnostic,
    alt: "Valise de diagnostic branchée sur la prise OBD, codes défaut à l'écran",
    texte: [
      "Voyant allumé, bruit suspect, perte de puissance ? La valise de diagnostic identifie les pannes moteur, électroniques, de transmission ou de freinage.",
      "Un bon diagnostic évite les réparations inutiles : on change la pièce qui est morte, pas celles qui pourraient l'être.",
    ],
  },
  {
    titre: "Freinage",
    image: IMAGES.freinage,
    alt: "Remplacement des plaquettes et du disque de frein, étrier en main",
    texte: [
      "Remplacement des plaquettes et des disques, inspection complète du circuit de freinage : étriers, flexibles, liquide de frein.",
      "Le freinage ne tolère aucun à-peu-près. Si vos freins grincent, vibrent ou que la pédale s'enfonce, ne tardez pas : passez à l'atelier.",
    ],
  },
  {
    titre: "Mécanique lourde",
    image: IMAGES.moteur,
    alt: "Intervention sur un moteur en atelier",
    texte: [
      "Réparations moteur, problèmes de transmission, interventions sur les systèmes complexes : distribution, embrayage, injection.",
      "Chaque travail lourd commence par un diagnostic et un devis écrit. Vous savez ce qui sera fait et combien ça coûtera avant la première vis.",
    ],
  },
];

export default function Mecanique() {
  const config = useConfig();
  const tarif = config?.tarifs?.mecanique || TARIFS_DEFAUT.mecanique;
  const { openDevis } = useDevis();

  return (
    <>
      <SEO
        title="Mécanique auto au Cannet — Vidange, freinage, diagnostic | Promo Pneus"
        description="Garage mécanique au Cannet, près de Cannes : vidange et révision dès 50 €, diagnostic auto dès 40 €, freinage dès 40 €. Toutes marques, sans rendez-vous."
      />
      <section className="bg-brand-dark py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
              {"//"} Chapitre 03
            </p>
            <h1 className="font-heading font-bold uppercase text-5xl sm:text-6xl md:text-7xl text-white tracking-wide leading-none">
              Mécanique
            </h1>
            <p className="mt-4 text-lg text-white/80 max-w-2xl">
              Vidange, révision, freinage, diagnostic. Toutes marques, devis avant toute réparation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <PricingTable titre={tarif.titre} rows={tarif.rows} testid="tarifs-mecanique" />
        </div>
      </section>

      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-24">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.titre}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <p className="font-heading font-bold text-6xl text-brand-light leading-none mb-2" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-5">
                    {s.titre}
                  </h2>
                  {s.texte.map((p, j) => (
                    <p key={j} className="text-brand-body leading-relaxed text-base md:text-lg mb-4">{p}</p>
                  ))}
                </div>
                <div className={`relative ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="absolute -top-3 -left-3 w-24 h-24 border-t-4 border-l-4 border-brand-red" aria-hidden="true" />
                  <img src={s.image} alt={s.alt} className="w-full h-72 sm:h-96 object-cover relative" loading="lazy" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 bg-brand-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-6">
              Pourquoi choisir Promo Pneus pour votre mécanique
            </h2>
            <p className="text-brand-body text-lg leading-relaxed mb-4">
              Un diagnostic avant un devis, jamais l'inverse. On vous explique ce qui est cassé, ce qui peut
              attendre, et ce que ça coûte. Vous décidez en connaissance de cause.
            </p>
            <p className="text-brand-body text-lg leading-relaxed mb-8">
              Ici, pas de discours commercial : du travail d'atelier, des pièces de qualité et une facture
              qui correspond au devis. Et si une réparation ne vaut pas le coup sur votre véhicule, on vous le dit franchement.
            </p>
            <button data-testid="meca-devis-bouton" onClick={() => openDevis("Demande concernant : mécanique. ")}
              className="bg-brand-red hover:bg-brand-redhover text-white font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 transition-colors">
              Demander un devis mécanique
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
